import axios from 'axios';
import * as Sentry from '@sentry/react';

import { isGatewayUnavailableStatus } from '../lib/sentryErrorFilter';

export const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

type TokenGetter = () => Promise<string | null>;
type SessionRenewer = () => Promise<boolean>;
let _getToken: TokenGetter | null = null;
let _cookieFirstEnabled = false;
let _renewSession: SessionRenewer | null = null;

let _cachedToken: string | null = null;
let _authGeneration = 0;

export function setTokenGetter(getter: TokenGetter) {
  _getToken = getter;
  _cachedToken = null;
  _authGeneration += 1;
}

/** Switch between the legacy bearer path and cookie-first Microsoft auth. */
export function configureCookieFirstAuth(enabled: boolean, renewer: SessionRenewer | null = null) {
  _cookieFirstEnabled = enabled;
  _renewSession = enabled ? renewer : null;
  _cachedToken = null;
  _authGeneration += 1;
}

async function resolveAndCacheToken(): Promise<string | null> {
  if (_cookieFirstEnabled || !_getToken) return null;
  const currentGen = _authGeneration;
  const getter = _getToken;
  const token = await getter();
  if (currentGen === _authGeneration && !_cookieFirstEnabled && _getToken === getter) {
    _cachedToken = token;
    return token;
  }
  return null;
}

/** Get auth headers for raw fetch() calls, such as streaming endpoints that cannot use axios. */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  if (_cookieFirstEnabled) return {};
  const token = await resolveAndCacheToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Synchronous auth headers for keepalive/unload calls where async promises may not resolve. */
export function getCachedAuthHeaders(): Record<string, string> {
  if (_cookieFirstEnabled || !_cachedToken) return {};
  return { Authorization: `Bearer ${_cachedToken}` };
}

api.interceptors.request.use(async (config) => {
  // Preserve intentionally supplied credentials (service integrations and
  // one-off bearer calls). Cookie-first requests otherwise send cookies only.
  if (!_cookieFirstEnabled && _getToken && !config.headers.Authorization) {
    const token = await resolveAndCacheToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

let _unauthorizedTimer: ReturnType<typeof setTimeout> | null = null;
function _dispatchUnauthorized() {
  if (!_unauthorizedTimer) {
    _unauthorizedTimer = setTimeout(() => {
      _unauthorizedTimer = null;
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }, 100);
  }
}

const _CB_WINDOW_MS = 30_000;
const _CB_THRESHOLD = 5;
const _CB_COOLDOWN_MS = 15_000;

let _cbErrors: number[] = [];
let _cbOpenUntil = 0;

export type CircuitBreakerErrorKind = 'server' | 'network';
let _cbLastKind: CircuitBreakerErrorKind = 'server';

/** Which side failed: the server answered 5xx, or nothing answered at all. */
export function _circuitBreakerErrorKind(
  status: number | undefined,
  hasResponse: boolean,
): CircuitBreakerErrorKind {
  return status !== undefined && hasResponse ? 'server' : 'network';
}

/**
 * What to tell the user while the breaker is open (#1600).
 *
 * "backend appears unavailable" is accurate and unhelpful: it reads as a fault
 * in whatever page they were on, so a routine deploy produces a bug report
 * against an innocent screen -- which is literally how #1576 was written.
 *
 * Master auto-deploys, so every merge restarts valentine.service and nginx
 * answers 502 for a minute. Those bursts appear on every retained day; one
 * window on 2026-08-28 produced 479 of them in four minutes. A 5xx therefore
 * almost always means "we are restarting", which is worth saying plainly.
 *
 * No response is different: it is as likely to be the user's own connection as
 * ours, so that wording accuses neither.
 */
export function _circuitBreakerMessage(kind: CircuitBreakerErrorKind): string {
  return kind === 'server'
    ? 'Valentine is restarting. Retrying shortly.'
    : 'Cannot reach Valentine - check your connection. Retrying shortly.';
}

function _cbRecordError(kind: CircuitBreakerErrorKind = 'server') {
  const now = Date.now();
  _cbLastKind = kind;
  _cbErrors.push(now);
  _cbErrors = _cbErrors.filter(t => now - t < _CB_WINDOW_MS);
  if (_cbErrors.length >= _CB_THRESHOLD) {
    _cbOpenUntil = now + _CB_COOLDOWN_MS;
    _cbErrors = [];
    Sentry.captureMessage('API circuit breaker tripped - 5 server errors in 30s', {
      level: 'error',
      tags: { source: 'circuit_breaker' },
    });
  }
}

function _cbIsOpen(): boolean {
  return Date.now() < _cbOpenUntil;
}

/**
 * Whether a failed response should count toward the "backend unavailable"
 * circuit breaker. A 503 is excluded ONLY when it carries Retry-After — the
 * HTTP-standard marker of an intentional, retryable "temporarily busy" state
 * (e.g. a Dropbox write throttle surfaced by the file router). Counting those
 * turned a transient Dropbox throttle into a firm-wide "backend appears
 * unavailable" cascade. A *bare* 503 — the response a proxy/load balancer
 * returns when there are no healthy backends — has no Retry-After and still
 * trips the breaker, as do 500/502/504 and network errors (no response).
 */
export function _shouldTripCircuitBreaker(
  status: number | undefined,
  hasResponse: boolean,
  isCancel: boolean,
  isRetryable: boolean,
): boolean {
  if (status !== undefined) {
    if (status === 503 && isRetryable) return false;
    return status >= 500;
  }
  return !hasResponse && !isCancel;
}

function _normalizeApiRoute(rawUrl: unknown): string {
  if (typeof rawUrl !== 'string' || !rawUrl) return 'unknown';
  const path = rawUrl.split('?')[0] || rawUrl;
  return path.replace(/\/\d+(?=\/|$)/g, '/{id}');
}

/**
 * Whether an individual failed request deserves its own Sentry event.
 *
 * 405 indicates a real client/server route disagreement. 404 is too noisy in SPAs.
 *
 * Gateway-unavailable statuses (502/503/504) are deliberately excluded: Valentine
 * auto-deploys ~14x/day, and for the ~16s of each restart every in-flight and polled
 * request from every open browser comes back 502. That is one condition, not N bugs, and
 * the circuit breaker below already reports it once per 30s window. Reporting per request
 * scaled the event count with deploy frequency and headcount instead of with anything
 * actually broken. A genuine application fault is a 500, which still reports.
 *
 * Network failures (no response — wifi drop, laptop sleep, closed tab, client offline)
 * are likewise excluded from per-request messages. They are client conditions, not API bugs,
 * and reporting every dropped background poll generated hundreds of spurious events
 * (VALENTINE-Z0, 685 events). The circuit breaker below already aggregates sustained network
 * unreachability and reports it as an outage condition.
 */
export function _shouldReportApiFailure(
  status: number | undefined,
  hasResponse: boolean,
): boolean {
  if (!hasResponse) return false;
  if (status === undefined) return false;
  if (isGatewayUnavailableStatus(status)) return false;
  return status >= 500 || status === 405;
}

function _reportInternalApiFailure(error: unknown) {
  if (axios.isCancel(error)) return;
  if (!axios.isAxiosError(error)) return;
  const status = error.response?.status as number | undefined;
  if (!_shouldReportApiFailure(status, Boolean(error.response))) return;

  const route = _normalizeApiRoute(error.config?.url);
  const method = String(error.config?.method || 'GET').toUpperCase();
  const failureKind = status && status >= 500 ? 'frontend_api_5xx' : 'contract_drift';
  Sentry.captureMessage('Valentine frontend API failure', {
    level: status && status >= 500 ? 'error' : 'warning',
    tags: {
      source_app: 'valentine-frontend',
      target_app: 'valentine',
      route,
      method,
      status_code: status ?? 'unknown',
      failure_kind: failureKind,
    },
  });
}

api.interceptors.request.use((config) => {
  const url = config.url || '';
  if (_cbIsOpen() && !url.includes('/health') && !url.includes('/auth/')) {
    return Promise.reject(new axios.CanceledError(_circuitBreakerMessage(_cbLastKind)));
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const hasRetryAfter = Boolean(error.response?.headers?.['retry-after']);
    if (_shouldTripCircuitBreaker(status, Boolean(error.response), axios.isCancel(error), hasRetryAfter)) {
      _cbRecordError(_circuitBreakerErrorKind(status, Boolean(error.response)));
    }

    if (
      error.response?.status === 401
      && _cookieFirstEnabled
      && _renewSession
      && error.config
      && !error.config._cookieRetried
    ) {
      try {
        error.config._cookieRetried = true;
        if (await _renewSession()) {
          return api.request(error.config);
        }
      } catch (refreshErr) {
        Sentry.captureException(refreshErr, { tags: { source: '401_cookie_retry' } });
      }
      _dispatchUnauthorized();
    } else if (error.response?.status === 401 && !_cookieFirstEnabled && _getToken && error.config && !error.config._retried) {
      try {
        const newToken = await _getToken();
        if (newToken) {
          error.config._retried = true;
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return api.request(error.config);
        }
      } catch (refreshErr) {
        Sentry.captureException(refreshErr, { tags: { source: '401_token_retry' } });
      }
      _dispatchUnauthorized();
    } else if (error.response?.status === 401) {
      _dispatchUnauthorized();
    }
    if (error.response?.status === 403 && error.response?.data?.detail === 'Account is deactivated') {
      window.dispatchEvent(new CustomEvent('auth:deactivated'));
    }
    _reportInternalApiFailure(error);
    return Promise.reject(error);
  },
);
